// Web-only wrapper around `@ping/enriched-text-input`. Adapts the
// EnrichedTextInput's contenteditable-based contract to the slimmer
// surface `ChatRoomComposer` consumes: a controlled-ish text value
// (markdown on the wire), focus/submit callbacks, a ref handle, and
// an active-format stream the floating format toolbar drives off.
//
// Platform note: this file imports `@ping/enriched-text-input` which
// resolves to its `.web.tsx` entry. Don't reference this component
// outside a `Platform.OS === 'web'` branch.

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { View, type StyleProp, type TextStyle } from 'react-native';
import {
  EnrichedTextInput,
  type EnrichedCodeLanguageContext,
  type EnrichedEditorState,
  type EnrichedFormat,
  type EnrichedLinkEditContext,
  type EnrichedTextInputRef,
  type EnrichedTriggerContext,
} from '@ping/enriched-text-input';
import type { RichTextFormat } from './RichTextFormat';

export interface EnrichedRichTextEditorHandle {
  focus(): void;
  blur(): void;
  toggleFormat(format: RichTextFormat): void;
  toggleBulletList(): void;
  toggleNumberedList(): void;
  setLink(url: string, label?: string): void;
  unwrapLink(): void;
  insertMention(displayName: string, id?: string): void;
  insertEmoji(glyph: string): void;
  /** Re-parse literal markup into rich formatting (Slack Cmd+Shift+F). */
  applyMarkdown(): void;
  /** Set the language of the code block containing the caret. */
  setCodeBlockLanguage(language: string): void;
}

/**
 * Editor-driven flag set covering the formats the toolbar tracks on
 * the web path. Mirrors `RichTextFormat` (the cross-platform enum)
 * PLUS bullet/numbered list + link — which the contenteditable
 * tracks natively but the cross-platform contract doesn't expose.
 */
export interface EnrichedActiveFormats {
  readonly formats: ReadonlySet<RichTextFormat>;
  readonly bulletList: boolean;
  readonly numberedList: boolean;
  readonly link: boolean;
  /**
   * Language of the code block at the caret (explicit or auto-detected),
   * or `null` when the caret is not inside a code block. Drives the
   * toolbar's language selector.
   */
  readonly codeLanguage: string | null;
  /**
   * `true` when the selection is caret-only (start === end), `false`
   * when the user has an actual range of text highlighted. The format
   * toolbar keys its visibility off this so it only appears while text
   * is selected — matching the Element/Matrix lexical composer's
   * selection-driven floating toolbar.
   */
  readonly selectionEmpty: boolean;
}

/** Structural equality for two active-format snapshots (order-independent set
 *  compare + the three flags + code language). Lets the editor skip emitting —
 *  and re-rendering the host — when nothing about the caret's formatting moved. */
function activeFormatsEqual(a: EnrichedActiveFormats, b: EnrichedActiveFormats): boolean {
  if (
    a.bulletList !== b.bulletList ||
    a.numberedList !== b.numberedList ||
    a.link !== b.link ||
    a.codeLanguage !== b.codeLanguage ||
    a.selectionEmpty !== b.selectionEmpty ||
    a.formats.size !== b.formats.size
  ) {
    return false;
  }
  for (const f of a.formats) {
    if (!b.formats.has(f)) return false;
  }
  return true;
}

export interface EnrichedRichTextEditorProps {
  /** Current markdown buffer owned by the host. */
  readonly text: string;
  readonly placeholder: string;
  readonly onChangeText: (next: string) => void;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  readonly onSubmit?: () => void;
  readonly pressEnterToSend?: boolean;
  /**
   * Active-format stream. Fires on every selection / content change
   * so the floating toolbar can highlight the right chips. Includes
   * the contract-pinned `RichTextFormat` set plus the new bullet /
   * numbered / link booleans.
   */
  readonly onActiveFormatsChange?: (state: EnrichedActiveFormats) => void;
  readonly disabled?: boolean;
  readonly textStyle?: StyleProp<TextStyle>;
  /** Hands a pasted (or dropped) image off to the host's attachment pipeline. */
  readonly onImagePaste?: (file: File) => void;
  /** Forwarded mention-trigger callbacks. See `@ping/enriched-text-input`. */
  readonly onMentionTrigger?: (ctx: EnrichedTriggerContext | null) => void;
  readonly onEmojiTrigger?: (ctx: EnrichedTriggerContext | null) => void;
  /** Click-on-link → opens the host's URL edit popover. */
  readonly onLinkEdit?: (ctx: EnrichedLinkEditContext) => void;
  /** Click on a code block's language chip → opens the language picker. */
  readonly onCodeLanguageOpen?: (ctx: EnrichedCodeLanguageContext) => void;
  /**
   * ↑ in an empty editor — Slack's "edit your last message". Forwarded
   * to the underlying contenteditable; when omitted, ↑ is plain caret
   * movement.
   */
  readonly onEditLast?: () => void;
  readonly testID?: string;
}

const FORMAT_MAP: Readonly<Record<EnrichedFormat, RichTextFormat | null>> = {
  bold: 'bold',
  italic: 'italic',
  strikethrough: 'strikethrough',
  inlineCode: 'inlineCode',
  quote: 'quote',
  bulletList: null,
  numberedList: null,
  codeBlock: 'codeBlock',
  link: null,
};

const EnrichedRichTextEditorInner = forwardRef<
  EnrichedRichTextEditorHandle,
  EnrichedRichTextEditorProps
>(function EnrichedRichTextEditor(
  {
    text,
    placeholder,
    onChangeText,
    onFocus,
    onBlur,
    onSubmit,
    pressEnterToSend = true,
    onActiveFormatsChange,
    disabled = false,
    textStyle,
    onImagePaste,
    onMentionTrigger,
    onEmojiTrigger,
    onLinkEdit,
    onCodeLanguageOpen,
    onEditLast,
    testID,
  },
  ref,
) {
  const innerRef = useRef<EnrichedTextInputRef | null>(null);
  // Track the last markdown value the contenteditable itself emitted
  // upstream. Re-renders that echo this value back as the `text`
  // prop are no-ops (the editor already has it). When the parent
  // pushes a value that DOESN'T match the last-emitted (e.g.
  // `setText("")` on send, host-driven draft replacement), we
  // detect it via the effect below and call the imperative
  // `setMarkdown` — which replaces the content without remounting
  // the contenteditable, so focus / IME / undo stay intact.
  const lastEmittedRef = useRef<string>(text);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => innerRef.current?.focus(),
      blur: () => innerRef.current?.blur(),
      toggleFormat: (format: RichTextFormat) => {
        const editor = innerRef.current;
        if (editor === null) return;
        switch (format) {
          case 'bold':
            editor.toggleBold();
            return;
          case 'italic':
            editor.toggleItalic();
            return;
          case 'strikethrough':
            editor.toggleStrikethrough();
            return;
          case 'inlineCode':
          case 'code':
            editor.toggleInlineCode();
            return;
          case 'quote':
            editor.toggleQuote();
            return;
          case 'codeBlock':
            editor.toggleCodeBlock();
            return;
        }
      },
      toggleBulletList: () => innerRef.current?.toggleBulletList(),
      toggleNumberedList: () => innerRef.current?.toggleNumberedList(),
      setLink: (url: string, label?: string) =>
        innerRef.current?.setLink(url, label),
      unwrapLink: () => innerRef.current?.unwrapLink(),
      insertMention: (displayName: string, id?: string) =>
        innerRef.current?.insertMention(displayName, id),
      insertEmoji: (glyph: string) => innerRef.current?.insertEmoji(glyph),
      applyMarkdown: () => innerRef.current?.applyMarkdown(),
      setCodeBlockLanguage: (language: string) =>
        innerRef.current?.setCodeBlockLanguage(language),
    }),
    [],
  );

  const handleChangeMarkdown = useCallback(
    (next: string) => {
      lastEmittedRef.current = next;
      onChangeText(next);
    },
    [onChangeText],
  );

  // External-change detector. When the parent pushes a `text` prop
  // value that the editor didn't itself emit, splice it into the
  // live DOM via the imperative `setMarkdown` method. Typing's
  // echo doesn't hit this branch because `lastEmittedRef.current`
  // already matches.
  useEffect(() => {
    if (text === lastEmittedRef.current) return;
    lastEmittedRef.current = text;
    innerRef.current?.setMarkdown(text);
  }, [text]);

  // Last emitted active-format snapshot — used to suppress no-op emits. Both
  // the editor's `onInput` and the document-level `selectionchange` fire on
  // every keystroke (the caret always moves on insert), and each previously
  // allocated a fresh object + Set → 2 host re-renders per keystroke even when
  // the active formats were identical. Skipping unchanged emits removes that.
  const lastActiveRef = useRef<EnrichedActiveFormats | null>(null);
  const handleChangeState = useCallback(
    (state: EnrichedEditorState) => {
      if (onActiveFormatsChange === undefined) return;
      const formats = new Set<RichTextFormat>();
      let bulletList = false;
      let numberedList = false;
      let link = false;
      for (const f of state.activeFormats) {
        if (f === 'bulletList') {
          bulletList = true;
          continue;
        }
        if (f === 'numberedList') {
          numberedList = true;
          continue;
        }
        if (f === 'link') {
          link = true;
          continue;
        }
        const mapped = FORMAT_MAP[f];
        if (mapped !== null) formats.add(mapped);
      }
      const next: EnrichedActiveFormats = {
        formats,
        bulletList,
        numberedList,
        link,
        codeLanguage: state.codeLanguage ?? null,
        selectionEmpty: state.selectionEmpty,
      };
      const prev = lastActiveRef.current;
      if (prev !== null && activeFormatsEqual(prev, next)) return;
      lastActiveRef.current = next;
      onActiveFormatsChange(next);
    },
    [onActiveFormatsChange],
  );

  // `exactOptionalPropertyTypes` rejects explicit `undefined` on
  // optional props, so we spread each host-supplied callback only
  // when it's defined.
  const optionalProps: Partial<{
    onFocus: () => void;
    onBlur: () => void;
    onSubmit: () => void;
    testID: string;
    onImagePaste: (file: File) => void;
    onMentionTrigger: (ctx: EnrichedTriggerContext | null) => void;
    onEmojiTrigger: (ctx: EnrichedTriggerContext | null) => void;
    onLinkEdit: (ctx: EnrichedLinkEditContext) => void;
    onCodeLanguageOpen: (ctx: EnrichedCodeLanguageContext) => void;
    onEditLast: () => void;
  }> = {};
  if (onFocus !== undefined) optionalProps.onFocus = onFocus;
  if (onBlur !== undefined) optionalProps.onBlur = onBlur;
  if (onSubmit !== undefined) optionalProps.onSubmit = onSubmit;
  if (testID !== undefined) optionalProps.testID = testID;
  if (onImagePaste !== undefined) optionalProps.onImagePaste = onImagePaste;
  if (onMentionTrigger !== undefined)
    optionalProps.onMentionTrigger = onMentionTrigger;
  if (onEmojiTrigger !== undefined)
    optionalProps.onEmojiTrigger = onEmojiTrigger;
  if (onLinkEdit !== undefined) optionalProps.onLinkEdit = onLinkEdit;
  if (onCodeLanguageOpen !== undefined)
    optionalProps.onCodeLanguageOpen = onCodeLanguageOpen;
  if (onEditLast !== undefined) optionalProps.onEditLast = onEditLast;

  return (
    <View testID={testID} style={textStyle}>
      <EnrichedTextInput
        ref={(node) => {
          innerRef.current = node;
        }}
        defaultValue={text}
        placeholder={placeholder}
        onChangeMarkdown={handleChangeMarkdown}
        onChangeState={handleChangeState}
        pressEnterToSend={pressEnterToSend}
        disabled={disabled}
        {...optionalProps}
      />
    </View>
  );
});

export const EnrichedRichTextEditor = React.memo(EnrichedRichTextEditorInner);
